import { useState } from "react";
import { getUtmParams, getVisitorId } from "../../lib/analytics";
import { calculateDscsaAssessment, type DscsaAssessment } from "../../../../shared/dscsaAssessment";

// ── Types ──────────────────────────────────────────────────────────────────────

type Answer = string;

interface FormData {
  firstName: string;
  pharmacyName: string;
  email: string;
  npi: string;
}

// ── Quiz questions ─────────────────────────────────────────────────────────────

const QUESTIONS = [
  {
    id: "q1",
    text: "Are you currently serializing and storing transaction data (T2) for 6 years?",
    options: ["Yes", "No", "Not Sure"],
  },
  {
    id: "q2",
    text: "Do you have a secure, automated way to verify NDCs upon delivery?",
    options: ["Yes", "No"],
  },
  {
    id: "q3",
    text: "Does your wholesale distributor provide records and support for your DSCSA workflow?",
    options: ["Yes", "No", "Not Sure"],
  },
] as const;

// Progress is based on completed questions (0 → 33 → 66 → 100%)
const QUESTION_COUNT = QUESTIONS.length;

// ── Sub-components ─────────────────────────────────────────────────────────────

function ProgressBar({ step }: { step: number }) {
  const pct = Math.round((step / QUESTION_COUNT) * 100);
  return (
    <div className="w-full bg-gray-200 rounded-full h-2 mb-6" role="progressbar" aria-label="DSCSA readiness assessment progress" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div
        className="h-2 rounded-full bg-emerald-500 transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function QuestionStep({
  question,
  stepIndex,
  onAnswer,
}: {
  question: (typeof QUESTIONS)[number];
  stepIndex: number;
  onAnswer: (answer: Answer) => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600">
        Question {stepIndex + 1} of {QUESTIONS.length}
      </p>
      <h3 className="text-xl sm:text-2xl font-bold text-primary leading-snug">
        {question.text}
      </h3>
      <div className="flex flex-col gap-3 mt-1">
        {question.options.map((option) => (
          <button
            key={option}
            onClick={() => onAnswer(option)}
            className="w-full text-left px-5 py-4 rounded-xl border-2 border-gray-200 text-primary font-semibold text-base hover:border-emerald-500 hover:bg-emerald-50 active:bg-emerald-100 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-emerald-400"
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function LeadForm({
  onSubmit,
}: {
  onSubmit: (data: FormData) => Promise<void>;
}) {
  const [form, setForm] = useState<FormData>({ firstName: "", pharmacyName: "", email: "", npi: "" });
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setForm((prev) => ({ ...prev, [name]: e.target.value }));
    // Clear the per-field error as the user types
    if (fieldErrors[name as keyof FormData]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = (): boolean => {
    const errors: Partial<Record<keyof FormData, string>> = {};
    if (!form.firstName.trim()) errors.firstName = "Please enter your first name.";
    if (!form.pharmacyName.trim()) errors.pharmacyName = "Please enter your pharmacy or clinic name.";
    if (!form.email.trim()) {
      errors.email = "Please enter your email address.";
    } else if (!EMAIL_RE.test(form.email.trim())) {
      errors.email = "Please enter a valid email address.";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) return;
    setLoading(true);
    try {
      await onSubmit({ ...form, firstName: form.firstName.trim(), pharmacyName: form.pharmacyName.trim(), email: form.email.trim(), npi: form.npi.trim() });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setSubmitError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <label htmlFor="quiz-firstName" className="text-sm font-medium text-primary">
          First Name <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="quiz-firstName"
          name="firstName"
          type="text"
          placeholder="Jane"
          value={form.firstName}
          onChange={handleChange}
          aria-describedby={fieldErrors.firstName ? "quiz-firstName-error" : undefined}
          className="input-field"
        />
        {fieldErrors.firstName && (
          <p id="quiz-firstName-error" role="alert" className="text-red-600 text-xs font-medium mt-0.5">
            {fieldErrors.firstName}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="quiz-pharmacyName" className="text-sm font-medium text-primary">
          Pharmacy / Clinic Name <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="quiz-pharmacyName"
          name="pharmacyName"
          type="text"
          placeholder="Main Street Pharmacy"
          value={form.pharmacyName}
          onChange={handleChange}
          aria-describedby={fieldErrors.pharmacyName ? "quiz-pharmacyName-error" : undefined}
          className="input-field"
        />
        {fieldErrors.pharmacyName && (
          <p id="quiz-pharmacyName-error" role="alert" className="text-red-600 text-xs font-medium mt-0.5">
            {fieldErrors.pharmacyName}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="quiz-email" className="text-sm font-medium text-primary">
          Email Address <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="quiz-email"
          name="email"
          type="email"
          placeholder="jane@mainstreetpharmacy.com"
          value={form.email}
          onChange={handleChange}
          aria-describedby={fieldErrors.email ? "quiz-email-error" : undefined}
          className="input-field"
        />
        {fieldErrors.email && (
          <p id="quiz-email-error" role="alert" className="text-red-600 text-xs font-medium mt-0.5">
            {fieldErrors.email}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="quiz-npi" className="text-sm font-medium text-primary">
          NPI or State License Number <span className="text-gray-600 font-normal">(optional — speeds up any follow-up review)</span>
        </label>
        <input
          id="quiz-npi"
          name="npi"
          type="text"
          placeholder="1234567890"
          value={form.npi}
          onChange={handleChange}
          aria-describedby={fieldErrors.npi ? "quiz-npi-error" : undefined}
          className="input-field"
        />
        {fieldErrors.npi && (
          <p id="quiz-npi-error" role="alert" className="text-red-600 text-xs font-medium mt-0.5">
            {fieldErrors.npi}
          </p>
        )}
      </div>
      {submitError && (
        <p role="alert" className="text-red-600 text-sm font-medium bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {submitError}
        </p>
      )}
      <button
        type="submit"
        disabled={loading}
        className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-base px-8 py-4 rounded-btn shadow-lg shadow-emerald-900/20 transition-colors duration-200"
      >
        {loading ? (
          <>
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Analyzing…
          </>
        ) : (
          <>
            Send My Readiness Result
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </>
        )}
      </button>
      <p className="text-xs text-gray-600 text-center">
        Takes under a minute. We'll email your result, followed by a short three-part DSCSA readiness series.
      </p>
    </form>
  );
}

function SuccessCard({ assessment }: { assessment: DscsaAssessment }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-labelledby="dscsa-quiz-success-heading"
      className="flex flex-col items-center gap-4 py-6 text-center"
    >
      <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
        <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 id="dscsa-quiz-success-heading" className="text-2xl font-bold text-primary">
         Your readiness result is queued
      </h3>
      <p className="text-gray-600 max-w-sm">
         We saved your answers and queued a general DSCSA readiness assessment for follow-up. Delivery timing can vary while email processing completes.
      </p>
      <div className="mt-2 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium px-4 py-2 rounded-full">
        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse inline-block" aria-hidden="true" />
         Result: {assessment.label}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

/**
 * DscsaQuiz — 3-question DSCSA readiness self-assessment funnel
 * followed by a lead-capture form that POSTs to /api/quiz-submit.
 *
 * Steps 0–2: one question each
 * Step 3: lead-capture form
 * submitted=true: success card
 *
 * Analytics callbacks (optional — parent wires them up):
 *   onStepAdvance(step, answer) — fired when the user picks an answer
 *   onLeadSubmit()              — fired on successful form submission
 */
export default function DscsaQuiz({
  onStepAdvance,
  onLeadSubmit,
}: {
  onStepAdvance?: (step: number, answer: string) => void;
  onLeadSubmit?: () => void;
}) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [submitted, setSubmitted] = useState(false);
  const [assessment, setAssessment] = useState<DscsaAssessment | null>(null);

  const handleAnswer = (answer: Answer) => {
    const question = QUESTIONS[step];
    setAnswers((prev) => ({ ...prev, [question.id]: answer }));
    onStepAdvance?.(step + 1, answer);
    setStep((prev) => prev + 1);
  };

  const handleFormSubmit = async (data: FormData) => {
    const utm = getUtmParams();
    const payload = {
      ...data,
      // Optional fields: omit empty strings so the server stores NULL, not "".
      pharmacyName: data.pharmacyName || undefined,
      npi: data.npi || undefined,
      answers,
      landingPage: window.location.pathname,
      visitorId: getVisitorId(),
      utmSource: utm.utmSource,
      utmMedium: utm.utmMedium,
      utmCampaign: utm.utmCampaign,
    };

    const res = await fetch("/api/quiz-submit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      let msg = "Something went wrong. Please try again.";
      try {
        const body = await res.json();
        if (typeof body?.error === "string") msg = body.error;
      } catch {
        // ignore parse error
      }
      throw new Error(msg);
    }

    const result = calculateDscsaAssessment(answers);
    onLeadSubmit?.();
    setAssessment(result);
    setSubmitted(true);
  };

  // Progress: 0→33→66→100% based on completed questions (cap at QUESTION_COUNT)
  const progressStep = submitted ? QUESTION_COUNT : Math.min(step, QUESTION_COUNT);

  return (
    <section id="readiness-assessment" className="bg-background py-16 lg:py-20 scroll-mt-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <div className="text-center mb-10">
          <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-2">
             Free DSCSA readiness check
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary leading-tight">
             Understand your DSCSA starting point{" "}
             <span className="text-emerald-600">in 3 quick questions.</span>
          </h2>
          <p className="mt-3 text-gray-500 text-base max-w-xl mx-auto">
             Answer 3 questions for a general readiness result. It is educational, not an audit, legal opinion, or compliance certification.
          </p>
        </div>

        {/* Quiz card */}
        <div className="card shadow-md p-8 sm:p-10">
          <ProgressBar step={progressStep} />

           {submitted && assessment ? (
             <SuccessCard assessment={assessment} />
          ) : step < QUESTIONS.length ? (
            <QuestionStep
              question={QUESTIONS[step]}
              stepIndex={step}
              onAnswer={handleAnswer}
            />
          ) : (
            <div className="flex flex-col gap-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-1">
                  Almost there!
                </p>
                <h3 className="text-xl sm:text-2xl font-bold text-primary leading-snug">
                  Where should we send your free readiness result?
                </h3>
                <p className="text-gray-500 text-sm mt-1">
                   We will email your general readiness result and a few practical review points. This is not a regulatory determination.
                </p>
              </div>
              <LeadForm onSubmit={handleFormSubmit} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
